import cn from 'classnames';
import { useEffect, ChangeEventHandler } from 'react';
import { useAppDispatch, useAppSelector } from '@app/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { createWallThicknessPolling, selectWallThicknessResult } from '@modules/wall-thickness';
import {
    viewerActions,
    selectCurrentViewerRenderMode,
    selectModelIsRendered,
    selectWtIsActive,
} from '@modules/model-viewer';
import { Switch } from '@react-md/form';
import { Typography } from '@react-md/typography';
import { Tooltipped } from '@react-md/tooltip';
import { Icons } from '@components/icons';
import { Alert, AlertHeading } from '@components/alert';
import { FeatureKeys as FF, useFeatureFlag } from '@components/feature-flags';
import { getNodeIdComposer, ModelViewerRenderMode } from '@utils';
import { ModelNotificationWallThicknessProps, WallThicknessNotPassedProps } from '../types';
import { AskForHelp } from '../../../ask-for-help';
import { ModelViewerPrefix } from '../../constants';

import styles from '../model-notifications.module.scss';

const _id = getNodeIdComposer(ModelViewerPrefix, 'wall_thickness');

export const ModelNotificationWallThickness = ({ model, materialId }: ModelNotificationWallThicknessProps) => {
    const dispatch = useAppDispatch();
    const wallThicknessResult = useAppSelector(selectWallThicknessResult);

    const onlyFailedShown = useFeatureFlag(FF.CartPage.WallThicknessOnlyFailedShown);

    useEffect(() => {
        // start wall thickness polling
        model.id && materialId && dispatch(createWallThicknessPolling());

        // todo move it to class?
        // reset
        dispatch(viewerActions.wtActivityChanged(false));
    }, [dispatch, model.id, materialId]);

    // todo check in selectWallThicknessFileUrl
    if (!wallThicknessResult || (onlyFailedShown && wallThicknessResult.status !== 'not_passed')) {
        return null;
    }

    switch (wallThicknessResult.status) {
        case 'not_processed':
            return null;
        case 'passed':
            return <WallThicknessPassed />;
        case 'not_passed':
            return (
                <WallThicknessNotPassed
                    model={model}
                    minimalWallThickness={wallThicknessResult.minimal_wall_thickness}
                />
            );
    }
};

const WallThicknessPassed = () => {
    const { t } = useTranslation();
    return (
        <Alert show className={styles.alert} variant="outlined" type="success">
            <AlertHeading customIcon={<Icons.CheckCircleTwo className="rmd-typography--theme-success" />}>
                <Typography type="body-2" component="span" className={styles.message}>
                    {t('messages.wallThicknessPassed', 'Wall thickness test passed')}
                </Typography>
            </AlertHeading>
        </Alert>
    );
};

const WallThicknessNotPassed = ({ model, minimalWallThickness }: WallThicknessNotPassedProps) => {
    const dispatch = useAppDispatch();
    const wtIsActive = useAppSelector(selectWtIsActive); // events setup, enable, disable or state? or just trigger wtIsChanged
    const modelIsRendered = useAppSelector(selectModelIsRendered);
    const currentViewerMode = useAppSelector(selectCurrentViewerRenderMode);

    // todo from viewer redux state selectIsWtEnabled = modelIsRendered + wallsIsLoaded + currentViewerMode === ModelViewerRenderMode.Solid
    const isReadyShowThinWalls = modelIsRendered && currentViewerMode === ModelViewerRenderMode.Solid;

    const handleChangeSwitch: ChangeEventHandler<HTMLInputElement> = ({ target: { checked } }) => {
        dispatch(viewerActions.wtActivityChanged(checked));
    };

    const { t } = useTranslation();

    return (
        <Alert show className={styles.alert} variant="outlined" type="error">
            <AlertHeading customIcon={<Icons.Slash className="rmd-typography--theme-error" />}>
                <div>
                    <div className={styles.message}>
                        <Typography type="body-1" component="span" className={styles.messageText}>
                            {t(
                                'messages.wallThicknessNotPassed',
                                'Walls are too thin, minimum recommended thickness value is {{minimal_wall_thickness}}',
                                {
                                    minimal_wall_thickness: minimalWallThickness,
                                },
                            )}
                        </Typography>
                        <Tooltipped
                            id={_id('hint')}
                            tooltip={
                                <Trans
                                    i18nKey="messages.wallThicknessAddNotif"
                                    defaults={
                                        'A wall thinner than {{minimal_wall_thickness}} mm will not be manufactured correctly, ' +
                                        'but will deform under its own weight or show up holes. However, you can place an order at your own risk. Or ask for help.'
                                    }
                                    values={{
                                        minimal_wall_thickness: minimalWallThickness,
                                    }}
                                />
                            }
                        >
                            <Icons.HelpCircle className={cn(styles.messageHelpIcon, 'rmd-typography--theme-primary')} />
                        </Tooltipped>
                    </div>
                    <Switch
                        id={_id('toggle_highlight')}
                        disabled={!isReadyShowThinWalls}
                        onChange={handleChangeSwitch}
                        checked={wtIsActive}
                        className={styles.switch}
                        ballClassName={styles.ball}
                        labelClassName={styles.switchLabel}
                        trackClassName={cn([styles.track, { [styles.trackActive]: wtIsActive }])}
                        label={
                            <Typography component="span" type="body-2">
                                {t('labels.showThinWalls', 'Show thin walls')}
                            </Typography>
                        }
                    />
                    <AskForHelp modelId={model.parent_model || model.id} />
                </div>
            </AlertHeading>
        </Alert>
    );
};
